import { AgeRestriction, ApkInfoWithTargets } from 'client/types';

import { AppDraftTrait, Draft } from './draft';
import { Image } from './image';
import { Operation } from './operation';
import {
    AliceSkillBackendSettings,
    AliceSkillPublishingSettings,
    BackendSettings,
    CommonSettings,
    InstallableAppSettings,
    LogoDto,
    PublishingSettings,
    SmartHomeBackendSettings,
    SmartHomePublishingSettings,
} from './settings';

export enum Channel {
    AliceSkill = 'aliceSkill',
    AutoApp = 'autoApp',
    SmartHome = 'smartHome',
    TvApp = 'tvApp',
    SmartScreenApp = 'smartScreenApp',
}

export interface DonationSettings {
    walletNumber: string;
    defaultSum: string;
}

export enum HomepageBadgeType {
    Kids = 'Kids',
    New = 'New',
    Puzzle = 'Puzzle',
    Quest = 'Quest',
    AliceChoice = 'AliceChoice',
    Helpful = 'Helpful',
    Sport = 'Sport',
    Interesting = 'Interesting',
    Wonderful = 'Wonderful',
    Tasty = 'Tasty',
}

export interface HomepageFields {
    tags: string[] | null;
    editorDescription: string | null;
    editorName: string | null;
    homepageBadgeTypes: HomepageBadgeType[] | null;
}

export enum Owner {
    USER = 'USER',
    PORG = 'PORG',
}

export interface GenericSkill<TChannel extends Channel, BS extends BackendSettings, PS extends PublishingSettings>
    extends CommonSettings<BS, PS>, HomepageFields {
    channel: TChannel;
    id: string;
    updatedAt: string;
    draft: TChannel extends Channel.TvApp | Channel.AutoApp | Channel.SmartScreenApp
        ? Draft<TChannel, BS, PS> & AppDraftTrait
        : Draft<TChannel, BS, PS>;
    operations: Operation[];
    onAir: boolean;
    user: { id: string; name: string; type: 'porg' | 'private' };
    userId: string;
    owner: { id: string; type: Owner; entityId: string };
    images: Image[];
    isBanned: boolean;
    isTrusted: boolean;
    slug: string;
    botGuid: string | null;
    donationSettings: DonationSettings | null;
    developerType: DeveloperType;
    canSkipReview: boolean;
    useZora: boolean;
    exposeInternalFlags: boolean;
    isRecommended: boolean;
    isTrustedSmartHomeSkill: boolean;
    openInNewTab: boolean;
    packageName: string;
    look: LookType;
    monitoringType: MonitoringType;
    revisions: (Draft<TChannel, BS, PS> &
        InstallableAppSettings &
        AppDraftTrait & { id: string; isActive: boolean; updatedAt: string; publishedAt: string })[];
    isVip: boolean;
    safeForKids: boolean;
}
export const getHomepageFields = (aliceApp: AliceApp) => {
    return {
        tags: aliceApp.tags ?? [],
        editorDescription: aliceApp.editorDescription ?? '',
        editorName: aliceApp.editorName ?? '',
        homepageBadgeTypes: aliceApp.homepageBadgeTypes ?? [],
    };
};

export enum DeveloperType {
    External = 'external',
    Yandex = 'yandex',
}

export enum LookType {
    External = 'external',
    Internal = 'internal',
}

export enum MonitoringType {
    Yandex = 'yandex',
    JustAI = 'justAI',
    Nonmonitored = 'nonmonitored',
}

export type AliceSkill = GenericSkill<Channel.AliceSkill, AliceSkillBackendSettings, AliceSkillPublishingSettings>;

interface AppSkillTrait {
    apkInfosWithTargets: ApkInfoWithTargets[];
    logoUrl: string;
    logos?: LogoDto[];
    secondaryLogoUrl?: string;
    packageName: string;
    screenshots: { imageUrl: string }[];
    ageRestriction?: AgeRestriction;
    firstPublishedAt?: string;
    targetPlatforms: string[];
    isTrusted: boolean;
}

export type AutoApp = GenericSkill<Channel.AutoApp, AliceSkillBackendSettings, AliceSkillPublishingSettings> &
    AppSkillTrait;

export type TvApp = GenericSkill<Channel.TvApp, AliceSkillBackendSettings, AliceSkillPublishingSettings> &
    AppSkillTrait;

export type SmartScreenApp = GenericSkill<
    Channel.SmartScreenApp,
    AliceSkillBackendSettings,
    AliceSkillPublishingSettings
> &
    AppSkillTrait;

export type InstallableApp = TvApp | AutoApp | SmartScreenApp;

export type SmartHome = GenericSkill<Channel.SmartHome, SmartHomeBackendSettings, SmartHomePublishingSettings>;

export type Skill = AliceSkill | SmartHome;

// for tvApp back compatibility with skill routes
export type AliceApp = Skill | InstallableApp;

export interface IAppCard {
    id: string;
    title: string;
    channel: Channel;
    onAir: boolean;
    publishedAt?: string;
    logoUrl?: string;
    firstScreenShotUrl?: string;
    lastOperation?: Operation;
}

export interface IInstallableAppPageInfo {
    previous?: number;
    next?: number;
}
